//changed due to package upgrade
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import AppInfoView from '@projectK/components/AppInfoView';
import AppTextField from '@projectK/components/AppTextField';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import IntlMessages from '@projectK/helpers/IntlMessages';
import * as yup from 'yup';
import { HandleMobileFormat } from '@projectK/ReUseFunctions';
import {
  getAuthenticatedProgressUserId,
  getSavedProgress,
  saveProgress,
  setTournamentWidgetReturnPath,
  setUserIdInLocalStorage,
  markTournamentRegistrationSignInExisting,
  completeTournamentLoginRedirect,
} from './handleState';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuthMethod, useAuthUser } from '@projectK/hooks/AuthHooks';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { Fonts } from '@projectK/constants/AppEnums';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';

const validationSchema = yup.object({
  first_name: yup
    .string()
    .max(100, () => <IntlMessages id='validation.firstNameMax' />)
    .matches(/^\S(?:.*\S)?$/, () => (
      <IntlMessages id='validation.firstNameNoSpaces' />
    ))
    .required(<IntlMessages id='validation.firstNameRequired' />),
  last_name: yup
    .string()
    .max(100, () => <IntlMessages id='validation.lastNameMax' />)
    .matches(/^\S(?:.*\S)?$/, () => (
      <IntlMessages id='validation.lastNameNoSpaces' />
    ))
    .required(<IntlMessages id='validation.lastNameRequired' />),
  email: yup
    .string()
    .max(100, () => <IntlMessages id='validation.emailMax' />)
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, () => (
      <IntlMessages id='validation.invalidEmailFormat' />
    ))
    .typeError(<IntlMessages id='validation.invalidEmail' />)
    .required(<IntlMessages id='validation.emailRequired' />),
  user_mobile: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
      <IntlMessages id='validation.invalidPhoneNumber' />
    ))
    .optional()
    .nullable(),
  password: yup
    .string()
    .required(<IntlMessages id='validation.passwordRequired' />)
    .min(8, () => <IntlMessages id='validation.passwordTooShort' />)
    .max(100, () => <IntlMessages id='validation.passwordMax' />)
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}[\]:;<>,.?~\\/-])/,
      () => <IntlMessages id='validation.passwordWeak' />,
    ),
  confirmPassword: yup
    .string()
    .max(100, () => <IntlMessages id='validation.confirmPasswordMax' />)
    .required(<IntlMessages id='validation.repeatPasswordRequired' />)
    .oneOf([yup.ref('password'), null], () => (
      <IntlMessages id='validation.passwordsDoNotMatch' />
    )),
  gender: yup.string().nullable().optional(),
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong' />)
    .required(<IntlMessages id='validation.addressRequired' />),
  state: yup.string().required(<IntlMessages id='validation.stateRequired' />),
  city: yup
    .string()
    .max(50, () => <IntlMessages id='validation.cityMax' />)
    .matches(/^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.cityFormat' />
    ))
    .required(<IntlMessages id='validation.cityRequired' />),
  country: yup
    .string()
    .required(<IntlMessages id='validation.countryRequired' />),
  zip_code: yup
    .string()
    .matches(/^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/, () => (
      <IntlMessages id='validation.zipCodeFormat' />
    ))
    .required(<IntlMessages id='validation.zipCodeRequired' />),
});

const SignUp = ({ onNext }) => {
  const { tournamentSlug } = useParams();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [userData, setUserData] = useState(null);
  const [showRetypeNewPassword, setShowRetypeNewPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [states, setStates] = useState([]);
  const [country, setCountry] = React.useState();
  const [userId, setUserId] = useState(null);
  const { user } = useAuthUser();
  const companyTheme = CompanyTheme();
  const navigate = useNavigate();
  const { signInUser } = useAuthMethod();
  const { setVerbiageSettings } = useApplicationSettings();

  const onShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const onDownNewPassword = (event) => {
    event.preventDefault();
  };

  const onShowRetypeNewPassword = () => {
    setShowRetypeNewPassword(!showRetypeNewPassword);
  };

  const onDownRetypeNewPassword = (event) => {
    event.preventDefault();
  };
  const fetchUsersData = async (userId) => {
    try {
      if (userId) {
        const response = await jwtAxios.get(
          `/tournament-insecure/user/${userId}`,
        );
        const { data } = response;
        setUserData(data);
      } else {
        setUserData(null);
      }
    } catch (error) {
      console.error('Error Fetching Users Data:', error);
    }
  };
  useEffect(() => {
    if (userId) {
      fetchUsersData(userId);
    }
  }, [userId]);

  useEffect(() => {
    fetchStates();
  }, [country]);

  useEffect(() => {
    const progressUserId = getAuthenticatedProgressUserId(user);
    if (!progressUserId) {
      return;
    }
    const savedProgress = getSavedProgress(tournamentSlug, progressUserId);
    if (savedProgress.data?.userId) {
      setUserId(savedProgress.data.userId);
    }
  }, [user?.id, tournamentSlug]);
  const fetchStates = async () => {
    try {
      const response = await jwtAxios.get(`/public/states`, {
        params: {
          country,
          perPage: 'all',
        },
      });
      const { data } = response;
      setStates(data.list);
    } catch (error) {
      setStates([]);
    }
  };

  return (
    <Container maxWidth='md'>
      <Formik
        enableReinitialize={true}
        initialValues={{
          first_name: userData?.first_name || '',
          last_name: userData?.last_name || '',
          email: userData?.email || '',
          user_mobile: HandleMobileFormat(userData?.user_mobile || '', true),
          password: null,
          is_active: true,
          address: userData?.address || '',
          city: userData?.city || '',
          state: userData?.state || '',
          zip_code: userData?.zip_code ? userData.zip_code.toString() : '',
          country: userData?.country || '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const {
            confirmPassword,
            user_mobile,
            roles,
            member_type,
            ...formData
          } = values;
          const modifiedFormData = {
            legal_first_name: null,
            legal_middle_name: null,
            legal_last_name: null,
            gender: values.gender || null,
            address: null,
            city: null,
            state: null,
            zip_code: null,
            country: null,
            user_mobile: values.user_mobile !== '' ? values.user_mobile : null,
            campus_id: null,
            roles: [14],
            is_active: values.is_active ? 1 : 0,
            ...formData,
          };

          setSubmitting(true);
          const isEdit = !!userId && !!userData;
          const url = isEdit
            ? `/tournament-insecure/user/${userData.id}`
            : `/tournament-insecure/user`;
          const method = isEdit ? 'put' : 'post';
          await jwtAxios[method](url, modifiedFormData)
            .then(async (response) => {
              setUserId(response?.data?.id);
              setSubmitting(false);
              saveProgress(
                tournamentSlug,
                0,
                {
                  signUp: values,
                  userId: response?.data?.id,
                },
                user?.id || response?.data?.id,
              );
              setUserIdInLocalStorage(response?.data?.id);
              if (!isEdit) {
                setTournamentWidgetReturnPath(tournamentSlug);
                await signInUser({
                  email: formData.email,
                  password: formData.password,
                  setVerbiageSettings,
                  isTournament: true,
                });
                completeTournamentLoginRedirect();
              }
              fetchUsersData();
              onNext({});
              setSubmitting(false);
            })
            .catch((error) => {
              infoViewActionsContext.fetchError(error.response.data.error);
              setSubmitting(false);
            });
        }}
      >
        {({ setFieldValue, values, resetForm, touched, errors }) => (
          <Form noValidate autoComplete='off'>
            <AppGridContainer sx={{ mt: 2 }}>
              <Grid size={{ xs: 12, md: 12 }}>
                <Box
                  sx={{
                    border: '1px solid #EAECF0',
                    p: 6,
                    borderRadius: 3,
                    boxShadow:
                      '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                  }}
                >
                  <Typography variant='h5' fontWeight='bold' gutterBottom>
                    Sign Up
                  </Typography>
                  <AppGridContainer sx={{ mt: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='first_name'
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`First Name`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        slotProps={{
                          input: { maxLength: 101 },
                        }}
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='last_name'
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`Last Name`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        slotProps={{
                          input: { maxLength: 101 },
                        }}
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='email'
                        variant='outlined'
                        value={values.email}
                        onChange={(event) => {
                          setFieldValue(
                            'email',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.email = true;
                        }}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`Email`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        slotProps={{
                          input: { maxLength: 101 },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='user_mobile'
                        variant='outlined'
                        slotProps={{
                          input: { maxLength: 12 },
                        }}
                        type='tel'
                        value={values.user_mobile || ''}
                        error={!!errors.user_mobile}
                        helperText={errors.user_mobile}
                        onChange={(event) => {
                          const formatted = HandleMobileFormat(event);
                          setFieldValue('user_mobile', formatted);
                        }}
                        sx={{ width: '100%', my: 2 }}
                        label='Mobile Number'
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='password'
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                aria-label='toggle password visibility'
                                onClick={onShowNewPassword}
                                onMouseDown={onDownNewPassword}
                                edge='end'
                              >
                                {showNewPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          onCopy: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                        }}
                        type={showNewPassword ? 'text' : 'password'}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`Password`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='confirmPassword'
                        type={showRetypeNewPassword ? 'text' : 'password'}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={onShowRetypeNewPassword}
                                  onMouseDown={onDownRetypeNewPassword}
                                  edge='end'
                                >
                                  {showRetypeNewPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`Confirm Password`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <TextField
                        name='address'
                        variant='outlined'
                        value={values.address}
                        onChange={(event) => {
                          setFieldValue(
                            'address',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.address = true;
                        }}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`Address`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        slotProps={{
                          input: { maxLength: 256 },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        sx={{
                          width: '100%',
                        }}
                        select
                        label={
                          <span>
                            {`Country`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        value={values.country}
                        error={touched.country && Boolean(errors.country)}
                        helperText={touched.country && errors.country}
                        onChange={(event) => {
                          setCountry(event.target.value);
                          setFieldValue('country', event.target.value); // Update the form field value
                        }}
                      >
                        <MenuItem value='us'>US</MenuItem>
                        <MenuItem value='ca'>CA</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Autocomplete
                        id='state-autocomplete'
                        options={states}
                        // getOptionLabel={(option) => option.name}
                        getOptionLabel={(option) =>
                          `${option.name} - ${option.abbreviation}`
                        }
                        value={
                          states?.find(
                            (state) => state.abbreviation === values.state,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setFieldValue(
                            'state',
                            newValue ? newValue.abbreviation : '',
                          );
                        }}
                        disabled={!values.country}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                {`State`}
                                <span style={{ color: 'red' }}> *</span>
                              </span>
                            }
                            variant='outlined'
                            error={touched.state && Boolean(errors.state)}
                            helperText={touched.state && errors.state}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='city'
                        variant='outlined'
                        value={values.city}
                        onChange={(event) => {
                          setFieldValue(
                            'city',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.city = true;
                        }}
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && errors.city}
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`City`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                        slotProps={{
                          input: { maxLength: 51 },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='zip_code'
                        variant='outlined'
                        value={values.zip_code}
                        onChange={(event) => {
                          setFieldValue(
                            'zip_code',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.zip_code = true;
                        }}
                        error={touched.zip_code && Boolean(errors.zip_code)}
                        helperText={touched.zip_code && errors.zip_code}
                        sx={{
                          width: '100%',
                        }}
                        label={
                          <span>
                            {`Zip Code`}
                            <span style={{ color: 'red' }}> *</span>
                          </span>
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mt: 2,
                          flexDirection: 'row',
                        }}
                      >
                        <Button
                          type='submit'
                          variant='contained'
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            width: '120px',
                            textTransform: 'none',
                            backgroundColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '& .MuiButton-startIcon': {
                              marginRight: '4px',
                            },
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? companyTheme.primary.dark
                                : '#1976d2',
                            },
                          }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Grid>
                  </AppGridContainer>
                </Box>
              </Grid>
            </AppGridContainer>
          </Form>
        )}
      </Formik>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 5,
          textAlign: 'center',
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          Already have an account?{' '}
          <Box
            component='span'
            sx={{
              color: companyTheme ? companyTheme.primary.main : 'text.primary',
              fontWeight: Fonts.MEDIUM,
              cursor: 'pointer',
            }}
            onClick={() => {
              markTournamentRegistrationSignInExisting();
              setTournamentWidgetReturnPath(tournamentSlug);
              navigate('/signin');
            }}
          >
            <IntlMessages id='common.signInHere' />
          </Box>
        </Typography>
      </Box>
      <AppInfoView />
    </Container>
  );
};
SignUp.propTypes = {
  onNext: PropTypes.func.isRequired,
};

export default SignUp;
